import React from 'react';

import { Button } from 'src/presentation/components/agent_desktop_components';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import LocalSmsMmsNumbersOnlyAlert from './LocalSmsMmsNumbersOnlyAlert';
import { PhoneNumberCapability, PhoneNumberType } from 'src/actions/availableNumbers/availableNumbersResource';
import { StageConfigState } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { StyledBold, StyledDescription, StyledRadioGroup } from './shared/elements';
import { useFormChange } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import { useResetFormState } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import Form, { useForm } from 'src/presentation/components/forms/Form';

const initialWorkingState = { activateVoice: true };

const ActivateVoice = ({
  nextStep,
  saveStep,
  stageConfig,
  phoneNumber,
}: {
  nextStep?;
  saveStep?;
  phoneNumber: string;
  stageConfig: StageConfigState;
}): JSX.Element | null => {
  const formContext = useForm({
    item: initialWorkingState,
    resetErrors: [],
  });

  const { updateState, workingState } = formContext;

  useFormChange(workingState, saveStep);
  useResetFormState(initialWorkingState, updateState, stageConfig);

  const {
    selectedPhone: { capabilities, phoneNumberType },
  } = stageConfig;
  const hasSmsMmsCapability =
    capabilities.includes(PhoneNumberCapability.SMS) || capabilities.includes(PhoneNumberCapability.MMS);

  const showAlert = workingState.activateVoice && hasSmsMmsCapability && phoneNumberType === PhoneNumberType.LOCAL;

  const activateDescription = (
    <>
      Activate voice capabilities for this number to start interacting with customers over phone.
      {showAlert && <LocalSmsMmsNumbersOnlyAlert />}
    </>
  );

  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Activate voice</H3>
        <StyledDescription>
          Do you want to activate voice capabilities for <StyledBold>{formatPhone(phoneNumber)}</StyledBold>?
        </StyledDescription>
        <Form context={formContext}>
          <StyledRadioGroup
            className="radioGroup"
            attr="activateVoice"
            data-aid="activate-voice-radio-group"
            options={[
              {
                label: 'Yes, activate voice',
                value: true,
                description: activateDescription,
              },
              {
                label: 'No, don’t activate voice',
                value: false,
                description: 'Maintain ownership of this number and reserve it for future use.',
              },
            ]}
          />
        </Form>
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-save" type={Button.Types.PRIMARY} onClick={nextStep}>
          Next
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

export default ActivateVoice;
